/* YU SEI — Account Build 02.4, panel state sync.
 * UI behaviours. API-backed changes live in yu-account-api.js. No journal persistence.
 * Tab-scoped storage: preview ID and a bounded presentation-only account hint.
 * No credentials, customer IDs, names or emails are added to storage or broadcasts.
 * A hint changes only navigation wording; it never authenticates or reveals data.
 */
(() => {
  'use strict';
  if (window.__yuMemberBuild02) return;
  window.__yuMemberBuild02 = true;
  document.documentElement.dataset.yuMemberUiBuild = '02.4';

  const PREVIEW_KEY = 'yu-member-preview-id-v1';
  const ALLOWED_TABS = ['orders', 'returns', 'profile', 'addresses', 'help', 'overview', 'journal', 'sunday', 'paper'];
  let previewId = null;
  const explicitId = new URL(window.location.href).searchParams.get('preview_theme_id');
  try {
    if (explicitId && /^\d{1,20}$/.test(explicitId)) {
      previewId = explicitId;
      sessionStorage.setItem(PREVIEW_KEY, JSON.stringify({ id: explicitId, expires: Date.now() + 14400000 }));
    } else {
      const saved = JSON.parse(sessionStorage.getItem(PREVIEW_KEY) || 'null');
      if (saved && /^\d{1,20}$/.test(saved.id) && saved.expires > Date.now()) previewId = saved.id;
    }
  } catch (_) { /* Storage may be unavailable; ordinary Shopify preview cookies still apply. */ }

  function sameOriginUrl(value) {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch (_) { return null; }
  }
  function relativeUrl(url) { return url.pathname + url.search + url.hash; }
  function prepareLink(link) {
    if (!(link instanceof HTMLAnchorElement)) return;
    const url = sameOriginUrl(link.getAttribute('href'));
    if (!url) return;
    // This changes account navigation only, never the OAuth callback setting.
    if (url.pathname === '/search' && url.searchParams.get('view') === 'yu-account') {
      url.pathname = '/pages/myaccount';
      url.searchParams.delete('view');
    }
    if (link.hasAttribute('data-yu-member-login')) {
      const returnTo = url.searchParams.get('return_to');
      if (url.searchParams.get('sign_in') === '1') { if (previewId) url.searchParams.set('preview_theme_id', previewId); link.href = relativeUrl(url); return; }
      if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//')) return;
      const destination = sameOriginUrl(returnTo);
      if (!destination) return;
      if (previewId) destination.searchParams.set('preview_theme_id', previewId);
      url.searchParams.set('return_to', relativeUrl(destination));
      link.href = relativeUrl(url);
    } else if (link.hasAttribute('data-yu-member-link') || link.hasAttribute('data-yu-member-order-page')) {
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      if (link.hasAttribute('data-yu-member-order-page')) {
        url.pathname = '/pages/myaccount';
        url.searchParams.delete('view');
        url.hash = 'orders';
      }
      link.href = relativeUrl(url);
    }
  }
  function prepareLinks(root = document) {
    root.querySelectorAll('[data-yu-member-login], [data-yu-member-link], [data-yu-member-order-page]').forEach(prepareLink);
  }
  prepareLinks();
  document.addEventListener('shopify:section:load', event => prepareLinks(event.target));
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a[data-yu-member-login], a[data-yu-member-link], a[data-yu-member-order-page]');
    if (link) {
      if(link.hasAttribute('data-yu-member-login')&&!event.defaultPrevented&&event.button===0&&!event.metaKey&&!event.ctrlKey&&!event.shiftKey&&!event.altKey){try{sessionStorage.removeItem('yu-account-signout-intent-01');}catch(_){}}
      prepareLink(link);
    }
  }, { capture: true });


  // 02.4: keep the global drawer and verified API-page UI consistent.
  // The hint survives same-tab storefront navigation, but is never used to make
  // API requests, authenticate a customer or render private/personal content.
  const ACCOUNT_HINT = 'yu-account-panel-hint-024';
  const ACCOUNT_RESUME = 'yu-account-resume-02';
  const ACCOUNT_SIGNOUT_INTENT = 'yu-account-signout-intent-01';
  const HINT_LIFETIME = 86400000;
  const accountStates = new Map();
  let accountUiChannel;
  try { accountUiChannel = new BroadcastChannel('yu-account-session-02'); } catch (_) {}
  function recentTime(at) {
    return Number.isFinite(at) && at <= Date.now() && Date.now() - at < HINT_LIFETIME;
  }
  function readAccountHint(clientId) {
    try {
      const hint = JSON.parse(sessionStorage.getItem(ACCOUNT_HINT) || 'null');
      if(hint?.clientId === clientId && ['recent', 'signed-out'].includes(hint.state) && recentTime(hint.at)) return hint.state;
      const resume = JSON.parse(sessionStorage.getItem(ACCOUNT_RESUME) || 'null');
      if(resume?.client === clientId && recentTime(resume.at)) return 'recent';
    } catch (_) {}
    return null;
  }
  function writeAccountHint(clientId, state, at=Date.now()) {
    if(!clientId || !['recent','signed-out'].includes(state) || !recentTime(at))return;
    try { sessionStorage.setItem(ACCOUNT_HINT, JSON.stringify({clientId,state,at})); } catch (_) {}
  }
  function accountPresentation(drawer) {
    const d=drawer.dataset;
    if(d.yuApiEnabled !== 'true' || !d.yuClientId)return d.yuNativeSignedIn === 'true' ? 'native' : 'signed-out';
    const current=accountStates.get(d.yuClientId);
    if(current)return current;
    const remembered=readAccountHint(d.yuClientId);
    if(remembered)return remembered;
    // Native theme authentication is a separate fallback, not an API token.
    return d.yuNativeSignedIn === 'true' ? 'recent' : 'signed-out';
  }
  function paintAccountDrawer(drawer) {
    if(drawer.dataset.yuDrawerBuild !== '02.4')return;
    const primary=drawer.querySelector('[data-yu-drawer-primary]');
    const label=drawer.querySelector('[data-yu-drawer-primary-label]');
    const secondary=drawer.querySelector('[data-yu-drawer-secondary]');
    const home=sameOriginUrl(drawer.dataset.yuAccountHome);
    const login=sameOriginUrl(drawer.dataset.yuAccountLogin);
    const logout=sameOriginUrl(drawer.dataset.yuAccountLogout);
    if(!primary || !label || !secondary || !home || !login)return;
    const mode=accountPresentation(drawer);
    const showAccount=['verified','recent','native'].includes(mode);
    drawer.dataset.yuPresentation=mode;
    primary.removeAttribute('data-yu-member-login');
    primary.removeAttribute('data-yu-member-link');
    primary.setAttribute(showAccount ? 'data-yu-member-link' : 'data-yu-member-login', '');
    primary.href=relativeUrl(showAccount ? home : login);
    label.textContent=showAccount ? 'View your account' : 'Sign in or create account';
    prepareLink(primary);
    secondary.removeAttribute('data-yu-member-login');
    secondary.removeAttribute('data-yu-member-link');
    secondary.removeAttribute('data-yu-member-signout');
    secondary.removeAttribute('data-yu-member-signout-request');
    secondary.hidden=false;
    if((mode==='verified' || mode==='native') && logout) {
      secondary.textContent='Sign out';
      secondary.href=relativeUrl(logout);
      secondary.setAttribute('data-yu-member-signout','');
    } else if(mode==='recent') {
      // This is an action request, not authentication or a native-only logout.
      const destination=new URL(home.href);destination.searchParams.delete('sign_in');
      if(previewId)destination.searchParams.set('preview_theme_id',previewId);
      secondary.textContent='Sign out';secondary.href=relativeUrl(destination);
      secondary.setAttribute('data-yu-member-signout-request','');
    } else if(showAccount) {
      // On product/home pages the API tokens deliberately are not loaded.
      // Never pretend to log that API session out via the native cookie alone.
      // Account settings returns to the verified account, which offers Sign out.
      const profile=new URL(home.href);profile.hash='profile';
      secondary.textContent='Account settings';secondary.href=relativeUrl(profile);
      secondary.setAttribute('data-yu-member-link','');prepareLink(secondary);
    } else {
      secondary.textContent='View the account preview';secondary.href=relativeUrl(home);
      secondary.setAttribute('data-yu-member-link','');prepareLink(secondary);
      secondary.hidden=drawer.dataset.yuPreviewNotice !== 'true';
    }
  }
  document.addEventListener('click', event => {
    const link=event.target instanceof Element?event.target.closest('[data-yu-member-signout-request]'):null;
    if(!link||event.defaultPrevented||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey)return;
    const drawer=link.closest('yu-member-drawer'),destination=sameOriginUrl(link.getAttribute('href'));
    if(!drawer||drawer.dataset.yuApiEnabled!=='true'||!drawer.dataset.yuClientId||!destination)return;
    event.preventDefault();
    try {
      sessionStorage.setItem(ACCOUNT_SIGNOUT_INTENT,JSON.stringify({action:'signout',clientId:drawer.dataset.yuClientId,at:Date.now()}));
    } catch (_) {
      // Leave a usable manual route and a visible, honest failure message.
      link.textContent='Open your account to sign out';link.removeAttribute('data-yu-member-signout-request');
      link.setAttribute('data-yu-member-link','');prepareLink(link);
      let message=drawer.querySelector('[data-yu-signout-message]');
      if(!message){message=document.createElement('p');message.dataset.yuSignoutMessage='';message.setAttribute('role','status');message.className='yu-member-fineprint';link.parentElement.append(message);}
      message.textContent='Sign-out could not start because browser session storage is unavailable. Open your account to sign out.';
      return;
    }
    const dialog=link.closest('dialog');if(dialog?.open)dialog.close();
    location.assign(relativeUrl(destination));
  });
  function paintAccountDrawers() {
    document.querySelectorAll('yu-member-drawer').forEach(paintAccountDrawer);
  }
  document.addEventListener('yu:account-presentation', event => {
    const value=event.detail;
    if(!value || typeof value.clientId!=='string' || !['verified','signed-out'].includes(value.state))return;
    if(!Array.from(document.querySelectorAll('yu-member-drawer')).some(d=>d.dataset.yuApiEnabled==='true' && d.dataset.yuClientId===value.clientId))return;
    accountStates.set(value.clientId, value.state);
    writeAccountHint(value.clientId, value.state==='verified' ? 'recent' : 'signed-out');
    paintAccountDrawers();
    if(value.state==='verified') {
      // Cross-tab messages are only a neutral navigation hint, never proof of login.
      try { accountUiChannel?.postMessage({type:'yu-account-ui-recent-024',clientId:value.clientId,at:Date.now()}); } catch (_) {}
    }
  });
  accountUiChannel?.addEventListener('message', event => {
    if(event.data==='logout') {
      document.querySelectorAll('yu-member-drawer[data-yu-api-enabled="true"]').forEach(d=>{
        accountStates.set(d.dataset.yuClientId,'signed-out');writeAccountHint(d.dataset.yuClientId,'signed-out');
      });
      // Do not let a stale per-tab resume flag re-open an explicitly ended session.
      try { sessionStorage.removeItem(ACCOUNT_RESUME); sessionStorage.removeItem(ACCOUNT_SIGNOUT_INTENT); } catch (_) {}
      paintAccountDrawers();return;
    }
    const value=event.data;
    if(value?.type!=='yu-account-ui-recent-024' || typeof value.clientId!=='string' || !recentTime(value.at))return;
    if(!Array.from(document.querySelectorAll('yu-member-drawer')).some(d=>d.dataset.yuApiEnabled==='true' && d.dataset.yuClientId===value.clientId))return;
    if(accountStates.get(value.clientId)==='verified')return;
    accountStates.set(value.clientId,'recent');writeAccountHint(value.clientId,'recent',value.at);paintAccountDrawers();
  });
  document.addEventListener('shopify:section:load', paintAccountDrawers);
  window.addEventListener('pageshow', paintAccountDrawers);
  window.addEventListener('focus', paintAccountDrawers);
  document.addEventListener('visibilitychange', () => {if(!document.hidden)paintAccountDrawers();});
  paintAccountDrawers();

  class YuMemberDrawer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      paintAccountDrawer(this);
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      document.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest('[data-yu-member-open]');
        if (!trigger) return;
        event.preventDefault();
        if (this.dialog.open) this.dialog.close();
        else this.open(trigger);
      }, options);
      this.dialog.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest('[data-yu-member-close]')) this.dialog.close();
        const link=target?.closest('a[data-yu-member-link]');
        const account=document.querySelector('[data-yu-account]');
        const destination=link ? sameOriginUrl(link.getAttribute('href')) : null;
        const current=new URL(window.location.href);
        const tab=destination?.hash.slice(1) || 'orders';
        if(link && account && typeof account.selectTab==='function' && !event.defaultPrevented && event.button===0 &&
           !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
           destination?.pathname===current.pathname && destination.searchParams.get('view')===current.searchParams.get('view') &&
           account.querySelector(`[data-yu-member-panel="${ALLOWED_TABS.includes(tab)?tab:'orders'}"]`)) {
          event.preventDefault();this.dialog.close();account.selectTab(tab,false,true);
        }
        if (target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
          if (outside) this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex="0"]'))
          .filter(element => element.getClientRects().length > 0);
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0];
        const last = controls[controls.length - 1];
        const active = document.activeElement;
        if (event.shiftKey && (active === first || !this.dialog.contains(active))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (active === last || !this.dialog.contains(active))) {
          event.preventDefault(); first.focus();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        document.querySelectorAll('[data-yu-member-open]').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, options);
      window.addEventListener('resize', () => { if (this.dialog.open) this.position(); }, { ...options, passive: true });
    }
    position() {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const bottom = this.opener?.getBoundingClientRect().bottom || 66;
      const top = Math.min(Math.max(bottom + 15, 12), Math.max(12, viewportHeight - 380));
      this.dialog.style.setProperty('--yu-member-sheet-top', `${Math.round(top)}px`);
      this.dialog.style.setProperty('--yu-member-sheet-max-height', `${Math.floor(viewportHeight - top - 16)}px`);
    }
    open(trigger) {
      paintAccountDrawer(this);
      this.opener = trigger;
      this.position();
      try {
        this.dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
      } catch (_) { window.location.assign(trigger.href); }
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
    }
  }

  class YuMemberSpace extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = Array.from(this.querySelectorAll('[data-yu-member-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-yu-member-panel]'));
      const nav = this.querySelector('.yu-member-nav');
      if (!nav || !this.tabs.length || !this.panels.length) return;
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', 'Your account sections');
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.yuMemberTab === 'addresses' ? 'profile' : tab.dataset.yuMemberTab);
      });
      this.panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', `YuMemberTab-${panel.dataset.yuMemberPanel}`);
        panel.setAttribute('tabindex', '0');
      });
      this.setAttribute('data-yu-member-enhanced', '');
      this.selectTab(this.currentHash(), false, false);
      window.addEventListener('hashchange', () => this.selectTab(this.currentHash(), false, false), options);
      this.addEventListener('click', event => {
        const target = event.target instanceof Element ? event.target : null;
        const tabLink = target?.closest('[data-yu-member-tab], [data-yu-member-goto]');
        if (tabLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
          const name = tabLink.dataset.yuMemberTab || tabLink.dataset.yuMemberGoto;
          if (ALLOWED_TABS.includes(name)) {
            event.preventDefault();
            this.selectTab(name, true, true);
          }
        }
        const mood = target?.closest('[data-yu-member-mood]');
        if (mood) {
          this.querySelectorAll('[data-yu-member-mood]').forEach(button => button.setAttribute('aria-pressed', String(button === mood)));
          const status = this.querySelector('[data-yu-member-mood-status]');
          if (status) status.textContent = `You chose ${mood.dataset.yuMemberMood.toLowerCase()}. Preview only - this check-in is not saved.`;
        }
        if (target?.closest('[data-yu-member-clear]')) {
          const reflection = this.querySelector('[data-yu-member-reflection]');
          if (reflection) { reflection.value = ''; reflection.focus(); }
          const status = this.querySelector('[data-yu-member-journal-status]');
          if (status) status.textContent = 'Preview writing cleared.';
        }
      }, options);
      nav.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.selectTab(this.tabs[next].dataset.yuMemberTab, true, true);
        }
      }, options);
      const orientation = () => nav.setAttribute('aria-orientation', window.matchMedia('(max-width: 749px)').matches ? 'horizontal' : 'vertical');
      orientation();
      window.addEventListener('resize', orientation, { ...options, passive: true });
    }
    currentHash() {
      const name = window.location.hash.slice(1);
      return this.tabs.some(tab => tab.dataset.yuMemberTab === name) ? name : 'orders';
    }
    selectTab(name, focus, updateUrl) {
      if (!this.tabs.some(tab => tab.dataset.yuMemberTab === name)) name = 'orders';
      this.tabs.forEach(tab => {
        const selected = tab.dataset.yuMemberTab === name;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        if (selected && focus) tab.focus({ preventScroll: true });
      });
      // Profile and Addresses are two visual views of the same verified private mount.
      // The API, form callbacks and session cleanup continue to own that mount.
      const panelName = name === 'addresses' ? 'profile' : name;
      this.dataset.yuAccountView = name;
      this.panels.forEach(panel => {
        panel.hidden = panel.dataset.yuMemberPanel !== panelName;
        if (panel.dataset.yuMemberPanel === 'profile') panel.setAttribute('aria-labelledby', 'YuMemberTab-' + (name === 'addresses' ? 'addresses' : 'profile'));
      });
      const profileTitle = this.querySelector('[data-yu-profile-title]');
      const profileCopy = this.querySelector('[data-yu-profile-copy]');
      if (profileTitle) profileTitle.textContent = name === 'addresses' ? 'Your addresses' : 'Your profile';
      if (profileCopy) profileCopy.textContent = name === 'addresses' ? 'Manage where you would like your orders delivered.' : 'Keep your personal details up to date.';
      this.dispatchEvent(new CustomEvent('yu:account-tab', {detail: {name}}));
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.hash = name;
        history.replaceState(history.state, '', relativeUrl(url));
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  if (!customElements.get('yu-member-drawer')) customElements.define('yu-member-drawer', YuMemberDrawer);
  if (!customElements.get('yu-member-space')) customElements.define('yu-member-space', YuMemberSpace);

  document.addEventListener('click', event => {
    const element = event.target instanceof Element ? event.target.closest('[data-yu-help-order], [data-yu-help-kind]') : null;
    if (!element) return;
    const order = document.querySelector('[data-yu-help-order-field]');
    const type = document.querySelector('[data-yu-help-type]');
    if (order && element.hasAttribute('data-yu-help-order')) order.value = element.dataset.yuHelpOrder || '';
    if (type && element.dataset.yuHelpKind) type.value = element.dataset.yuHelpKind;
  });
  const helpReturn = document.querySelector('#YuAccountHelpForm input[name="return_to"]');
  if (helpReturn && previewId) {
    const destination = sameOriginUrl(helpReturn.value);
    if (destination) { destination.searchParams.set('preview_theme_id', previewId); helpReturn.value = relativeUrl(destination); }
  }
  if (document.querySelector('[data-yu-support-success], [data-yu-support-errors], #YuAccountHelpForm .errors')) {
    document.querySelector('[data-yu-member-space]')?.selectTab('help', false, true);
  }
  // Do not restore a previous customer's private DOM from back/forward cache.
  // This is an extra UI precaution; Shopify's server-side customer session is
  // the access-control boundary. No sensitive values are placed in Web Storage.
  window.addEventListener('pagehide', () => {
    document.querySelectorAll('[data-yu-member-reflection]').forEach(input => { input.value = ''; });
    if (document.querySelector('[data-yu-private]')) document.documentElement.classList.add('yu-member-restoring');
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted && document.querySelector('[data-yu-private]')) window.location.reload();
    else document.documentElement.classList.remove('yu-member-restoring');
  });
})();
